import { config } from '@/config';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { Tracking } from '@/types/analytics';
import {
  bookingFlowPropertiesSchema,
  ErrorType,
  InitiatedFrom,
  ValidationErrorType
} from '@/types/analytics/booking-flow';
import { ConfirmedBooking } from '@/types/api/services/booking';
import { ClientBundle } from '@/types/api/services/users/schema';
import { CheckoutEmployee, CheckoutPaymentMethod, CheckoutSummary, SelectedPaymentMethod } from '@/types/checkout';
import { BookState, Giftcard } from '@/types/state/book';
import { BundleState } from '@/types/state/bundle';
import { FormField, Place } from '@/types/state/shared';
import { User } from '@/types/user';
import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { isDealsBooking } from './booking';
import { getBookingEmployee } from './confirmation';
import { getDaysUntilDateTime } from './date';
import { isServer } from './general';
import {
  getDynamicPriceListIdKey,
  getServiceCampaignIfAny,
  getServicePrice,
  getStartTime,
  getTotalPrice
} from './placeHelper';
import { hasDealCampagin } from './placeHelperTS';

type Flow = 'booking_flow' | 'payment_activation_flow' | 'buy_bundle_flow';

type FlowEventMap = {
  booking_flow: Tracking.BookingFlow;
  payment_activation_flow: Tracking.PaymentActivationFlow;
  buy_bundle_flow: Tracking.BuyBundleFlow;
};

type FlowEvent<F extends Flow> = FlowEventMap[F];
type FlowProps<F extends Flow> = FlowEvent<F>['properties'];
type State = z.infer<typeof sessionStateSchema> & Partial<FlowProps<Flow>>;

/**
 * Props that can be manually set on the flow tracking session
 * (excluding initiated_from and booking_session_id which are managed internally)
 */
type SettableFlowProps<F extends Flow> = Omit<FlowProps<F>, 'initiated_from' | 'booking_session_id'>;

export interface CheckoutFlowTrackingSessionMethodsBase<F extends Flow> {
  getId(): State['id'] | undefined;
  getCompanyId(): State['company_id'];
  isActive(): State['active'];
  getStepCategory(): State['step_category'] | undefined;
  initiate(
    place: Pick<Place, 'id' | 'about' | 'lastMinuteDiscount' | 'campaigns'>,
    user: User | null,
    initiatedFrom: InitiatedFrom,
    props?: SettableFlowProps<F>,
  ): void;
  bookingCTAClicked(): void;
  terms(step: 'initiated' | 'closed', step_category: 'T&C_merchant' | 'T&C_bd'): void;
  checkoutPaymentMethod(
    step?: 'initiated' | 'success' | 'error' | 'cancel',
    payment_method?: 'google_pay' | 'apple_pay' | 'swish' | 'klarna' | 'walley' | 'qliro' | 'card',
  ): void;
  validationError(error_type: ValidationErrorType[]): void;
  error(error_type: ErrorType[]): void;
  cancelSession(): void;
  checkoutConfirmation(
    props: { type: 'booking'; data: ConfirmedBooking } | { type: 'bundle'; data: ClientBundle },
  ): void;
  setProperties(props: SettableFlowProps<F>): void;
}

interface BookingFlowTrackingSessionMethods {
  appendTimeslotsViewed(timeslots: string[]): void;
  searchTime(): void;
  pickTime(): void;
  summaryViewed(): void;
  backToServiceSelection(): void;
  backToEmployeeSelection(): void;
  backToAddOnSelection(): void;
  backToTimepicker(): void;
  viewServiceDetails(step: 'initiated' | 'cancel', bookable: boolean): void;
}

const TRACKING_SESSION_STORAGE_KEY = 'trk_session';
const TAB_ID_KEY = 'trk_tab_id';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Mapping of flow to session id property key
 */
const SESSION_ID_KEY: Record<Flow, 'booking_session_id' | 'payment_activation_session_id' | 'buy_bundle_session_id'> = {
  booking_flow: 'booking_session_id',
  payment_activation_flow: 'payment_activation_session_id',
  buy_bundle_flow: 'buy_bundle_session_id',
};

/**
 * @Note to future selves:
 * Reason for using localStorage for session persistence:
 * How we define a "session" for tracking purposes is different then how sessionStorage lets us persist data.
 * SessionStorage is cleared on mobile browsers like IOS safari for example when the tab is put in the background for a certian amount of time,
 * or if user navigates to BankID or another app, the sessionStorage can be cleared since due to device memory management
 *
 * Solution:
 * Get or create a unique tab identifier using window.name and use that to fetch from and save to localStorage
 * This persists the session in a more reliable way for our use case.
 */
function getTabId(): string {
  if (isServer) return 'server';

  // Check if tabId already exists in window.name
  if (window.name && window.name.startsWith(TAB_ID_KEY)) {
    return window.name.split('=')[1];
  }

  // generat new tabId and store in window.name
  const tabId = uuidv4();
  window.name = `${TAB_ID_KEY}=${tabId}`;
  return tabId;
}

function getTrackingSessionStorageKey(flow: Flow) {
  const tabId = getTabId();
  return `${TRACKING_SESSION_STORAGE_KEY}_${flow}_${tabId}`;
}

const sessionStateSchema = z
  .object({
    id: z.string().uuid(),
    initiated_from: z.string(),
    active: z.boolean(),
    createdAt: z.number(),
    /**
     * Sets the timeslots that have been viewed.
     *
     * We need to keep track of this in the session so that
     * we can keep track of the number of unique timeslots viewed
     *
     * @param timeslotsViewed - Array of ISO 8601 datetime strings
     *   (e.g. `['2026-01-21T16:30:00+01:00', '2026-01-21T17:00:00+01:00']`)
     */
    timeslotsViewed: z.array(z.string()).optional(),
  })
  .merge(bookingFlowPropertiesSchema.omit({ initiated_from: true, booking_session_id: true }));

class CheckoutFlowTrackingSessionBase<F extends Flow> implements CheckoutFlowTrackingSessionMethodsBase<F> {
  protected state: State | null = null;

  constructor(private readonly flow: F) {
    this.flow = flow;
    this.load();
  }

  /**
   * Initiate tracking for booking flow event
   */
  initiate: CheckoutFlowTrackingSessionMethodsBase<F>['initiate'] = (place, user, initiatedFrom, props) => {
    /**
     * end any existing session first
     */
    if (this.isActive()) {
      this.end();
    }

    const properties: FlowProps<F> = {
      company_id: place.id,
      company_subscription_type: place.about?.settings?.subscriptionType as any,
      step: 'initiated',
      step_category: 'selection',
      ...props,
    };

    const has_previous_bookings_at_company = (user?.clientPlaceBookings ?? []).includes(place.id);

    if (this.flow === 'booking_flow') {
      if (place.lastMinuteDiscount) {
        properties['booking_type'] = 'sistaminuten';
      } else if (hasDealCampagin(place.campaigns)) {
        properties['booking_type'] = 'deal';
      } else {
        properties['booking_type'] = 'marketplace';
      }
      properties['has_previous_bookings_at_company'] = has_previous_bookings_at_company;
    }

    if (this.flow === 'buy_bundle_flow') {
      properties['has_previous_bookings_at_company'] = has_previous_bookings_at_company;
    }

    this.start(initiatedFrom);
    this.track(properties);
  };

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  bookingCTAClicked() {
    this.track({ step: 'booking_cta', step_category: 'summary' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  terms(step: 'initiated' | 'closed', step_category: 'T&C_merchant' | 'T&C_bd') {
    this.track({ step, step_category });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  checkoutPaymentMethod(
    step: 'initiated' | 'success' | 'error' | 'cancel' = 'initiated',
    payment_method?: 'google_pay' | 'apple_pay' | 'swish' | 'klarna' | 'walley' | 'qliro' | 'card',
    options?: Tracking.EventOptions,
  ) {
    this.track({ step, step_category: 'checkout_payment_methods', payment_method }, options);
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  validationError(error_type: ValidationErrorType[]) {
    this.track({ step: 'validation_error', step_category: 'summary', error_type });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  error(error_type: ErrorType[]) {
    this.track({ step: 'error', step_category: 'summary', error_type });
  }

  cancelSession() {
    this.track({ step: 'cancel', step_category: 'selection' });
    this.end();
  }

  checkoutConfirmation: CheckoutFlowTrackingSessionMethodsBase<F>['checkoutConfirmation'] = (resource) => {
    switch (resource.type) {
      case 'booking': {
        const booking = resource.data;
        const employee = getBookingEmployee(booking);
        const { giftcards, appliedBundle, campaigns, onlineFee } = booking.extra;
        const pickedProducts = (campaigns || []).flatMap((campaign) => campaign.products ?? []);

        const [productPrices, productPricesWithoutCampaigns] = [
          pickedProducts.map((p) => ({ price: p.price })),
          pickedProducts.filter((p) => p.originalPrice !== undefined).map((p) => ({ price: p.originalPrice })),
        ];

        const total = +getTotalPrice(
          booking.services,
          booking.place,
          employee.about?.priceListId,
          [],
          0,
          1,
          false,
          [],
          false,
          getDynamicPriceListIdKey(booking),
          undefined,
          onlineFee,
          productPricesWithoutCampaigns,
        );

        const final = +getTotalPrice(
          booking.services,
          booking.place,
          employee.about?.priceListId,
          giftcards || [],
          0,
          1,
          false,
          campaigns || [],
          false,
          getDynamicPriceListIdKey(booking),
          appliedBundle,
          onlineFee,
          productPrices,
        );

        const finalWithoutGiftcards = +getTotalPrice(
          booking.services,
          booking.place,
          employee.about?.priceListId,
          [],
          0,
          1,
          false,
          campaigns || [],
          false,
          getDynamicPriceListIdKey(booking),
          appliedBundle,
          onlineFee,
          productPrices,
        );

        const isFree = finalWithoutGiftcards === 0;

        const selected_payment_method: Tracking.BookingFlow['properties']['selected_payment_method'] = [];

        switch (booking.extra.paymentMethod) {
          case CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE:
            isFree ? selected_payment_method.push('free') : selected_payment_method.push('pay_in_salon');
            break;
          case CHECKOUT_PAYMENT_METHOD.KLARNA:
            selected_payment_method.push('klarna');
            break;
          case CHECKOUT_PAYMENT_METHOD.QLIRO:
            selected_payment_method.push('qliro');
            break;
          case CHECKOUT_PAYMENT_METHOD.STORED_COF:
            selected_payment_method.push('card');
            break;
          case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
            selected_payment_method.push('google_pay');
            break;
          case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
            selected_payment_method.push('apple_pay');
            break;
          case CHECKOUT_PAYMENT_METHOD.SWISH:
            selected_payment_method.push('swish');
            break;
          case CHECKOUT_PAYMENT_METHOD.WALLEY:
            selected_payment_method.push('walley');
            break;
          default:
            break;
        }

        if (booking.extra.giftcards.length > 0) {
          const appliedGiftcard = booking.extra.giftcards.find((giftcard) => giftcard.code.startsWith('U-'));
          const appliedWellnessCard = booking.extra.giftcards.find((giftcard) => giftcard.code.startsWith('F-'));
          const appliedValueCard = booking.extra.giftcards.find((giftcard) => giftcard.code.startsWith('V-'));

          if (appliedGiftcard) {
            selected_payment_method.push('gift_card');
          }

          if (appliedWellnessCard) {
            selected_payment_method.push('wellness_card');
          }

          if (appliedValueCard) {
            selected_payment_method.push('value_card');
          }
        }

        if (booking.extra.appliedBundle) {
          selected_payment_method.push('bundle');
        }

        const isRaketen = Boolean(booking.extra.bidId);
        const isPaidOnline = selected_payment_method.some((method) => method !== 'pay_in_salon');
        const isSistaminutentiderBooking = booking.extra.isSistaminutentiderBooking;

        let monetization_source: Tracking.BookingFlow['properties']['monetization_source'] = [];
        let has_campaigns = booking.extra.campaigns && booking.extra.campaigns.length > 0;
        let booking_type: Tracking.BookingFlow['properties']['booking_type'] = undefined;

        if (isRaketen) {
          monetization_source.push('Raketen');
        }

        if (isSistaminutentiderBooking) {
          monetization_source.push('SMT');
          booking_type = 'sistaminuten';
        } else if (isDealsBooking(booking)) {
          monetization_source.push('deals');
          booking_type = 'deal';
        } else {
          booking_type = 'marketplace';
        }

        if (isPaidOnline) {
          monetization_source.push('payment_solution');
        }

        const properties: Tracking.BookingFlow['properties'] = {
          step: 'checkout_confirmation',
          step_category: 'checkout_confirmation',
          booking_id: booking.id,
          monetization_source,
          has_campaigns,
          ...(!Number.isNaN(total) ? { gmv: total } : {}),
          ...(!Number.isNaN(final) ? { paid_price: final } : {}),
          selected_payment_method,
          employee_selection: employee.id === 0 ? 'any' : 'specific',
          booking_type,
        };

        this.track(properties);
        this.end();
        return;
      }
      case 'bundle': {
        const bundle = resource.data;
        const properties: Tracking.BuyBundleFlow['properties'] = {
          step: 'checkout_confirmation',
          step_category: 'checkout_confirmation',
          bundle_id: bundle.id,
          gmv: +bundle.price,
          paid_price: bundle.finalPrice ? +bundle.finalPrice : +bundle.price,
        };

        this.track(properties);

        this.end();
      }
    }
  };

  getId: CheckoutFlowTrackingSessionMethodsBase<F>['getId'] = () => {
    return this.state?.id;
  };

  getCompanyId: CheckoutFlowTrackingSessionMethodsBase<F>['getCompanyId'] = () => {
    return this.state?.company_id;
  };

  getStepCategory: CheckoutFlowTrackingSessionMethodsBase<F>['getStepCategory'] = () => {
    return this.state?.step_category;
  };

  isActive: CheckoutFlowTrackingSessionMethodsBase<F>['isActive'] = () => {
    return Boolean(this.state?.active);
  };

  setProperties: CheckoutFlowTrackingSessionMethodsBase<F>['setProperties'] = (properties) => {
    this.state = { ...(this.state || {}), ...properties };
    this.saveSessionInLocalStorage();
  };

  clearTrackingSession() {
    if (!this.canUseStorage) return;
    localStorage.removeItem(getTrackingSessionStorageKey(this.flow));
  }

  protected start(initiated_from: InitiatedFrom, options?: { force: boolean; id?: string }): State['id'] {
    if (this.state?.active && !options?.force) {
      return this.state.id;
    }
    const id = options?.id ?? uuidv4();
    const state = { id, initiated_from, active: true, createdAt: Date.now() };
    this.state = state;

    // Clean up expired sessions from other tabs when starting a new one
    this.cleanupExpiredSessions();

    return id;
  }

  protected end(): void {
    this.clearTrackingSession();
    if (!this.state?.active) return;
    this.state = null;
  }

  protected track(props: Partial<FlowProps<F>>, options?: Tracking.EventOptions): void {
    if (!this.isActive() || !this.state) {
      console.warn('[checkoutFlowTracking] No active session — event skipped', props);

      if (config.env === 'production') {
        Sentry.captureMessage(
          `[tracking/${this.flow}] No active session step=${props.step} step_category=${props.step_category}`,
        );
      }
      return;
    }

    const { id, initiated_from, active, createdAt, timeslotsViewed, ...existingProperties } = this.state;

    /**
     * remove short lived properties
     */
    const { payment_method, error_type, error_message, ...keep } = props;

    if (existingProperties) {
      this.state = { ...this.state, ...existingProperties, ...keep };
    }
    this.saveSessionInLocalStorage();

    const properties: FlowProps<F> = {
      [SESSION_ID_KEY[this.flow]]: id,
      initiated_from,
      ...existingProperties,
      ...props,
    };

    const event = {
      name: this.flow,
      properties,
    } as FlowEvent<F>;

    if (config.env === 'development') {
      console.debug(`[checkoutFlowTrackingSession] track event=${event.name}`, event.properties);
    }

    // Dispatch custom event for debug overlay
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('flow-tracking-event', {
          detail: { flow: this.flow, step: props.step, step_category: props.step_category, properties },
        }),
      );
    }

    trackMpEvent(event, options);
  }

  protected get canUseStorage() {
    return !isServer && typeof window !== 'undefined';
  }

  protected load(): State | null {
    if (!this.canUseStorage) return null;

    const raw = localStorage.getItem(getTrackingSessionStorageKey(this.flow));

    if (!raw) {
      this.state = null;
      return null;
    }

    const parsed = sessionStateSchema.safeParse(JSON.parse(raw));

    if (!parsed.success) {
      console.debug('[checkoutFlowTrackingSession] load from storage failed', parsed);

      if (config.env !== 'development') {
        Sentry.captureMessage(
          `[tracking/${this.flow}] Failed to parse tracking session from storage: ${JSON.stringify(
            parsed.error.format(),
          )}`,
        );
      }

      this.state = null;
      return null;
    }

    this.state = parsed.data;
    return parsed.data;
  }

  protected saveSessionInLocalStorage() {
    if (!this.canUseStorage) return;
    localStorage.setItem(getTrackingSessionStorageKey(this.flow), JSON.stringify(this.state));
  }

  /**
   * Clean up expired tracking sessions from localStorage
   * This removes sessions from all tabs/flows that have exceeded TTL
   */
  protected cleanupExpiredSessions(): void {
    if (!this.canUseStorage) return;

    try {
      const now = Date.now();
      const keysToRemove: string[] = [];

      // Find all tracking session keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(TRACKING_SESSION_STORAGE_KEY)) continue;

        const raw = localStorage.getItem(key);
        if (!raw) continue;

        try {
          const data = JSON.parse(raw);
          const createdAt = data.createdAt;

          if (typeof createdAt === 'number' && now - createdAt > SESSION_TTL_MS) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // Invalid JSON, remove it
          keysToRemove.push(key);
        }
      }

      // Remove expired sessions
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (e) {}
  }

  protected clearSummaryProperties() {
    if (!this.state) return;

    const {
      available_payment_methods,
      preselected_payment_method,
      selected_payment_method,
      days_until_booking,
      number_of_required_fields,
      monetization_source,
      has_campaigns,
      is_bokadirekt_pay,
      platform_fee,
      ...rest
    } = this.state;

    this.state = { ...rest };
    this.saveSessionInLocalStorage();
  }
}

function mapPaymentMethod(
  method?: CheckoutPaymentMethod,
): Tracking.BookingFlow['properties']['available_payment_methods'][number] | null {
  switch (method) {
    case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
      return 'apple_pay';
    case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
      return 'google_pay';
    case CHECKOUT_PAYMENT_METHOD.KLARNA:
      return 'klarna';
    case CHECKOUT_PAYMENT_METHOD.QLIRO:
      return 'qliro';
    case CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE:
      return 'pay_in_salon';
    case CHECKOUT_PAYMENT_METHOD.SWISH:
      return 'swish';
    case CHECKOUT_PAYMENT_METHOD.WALLEY:
      return 'walley';
    case CHECKOUT_PAYMENT_METHOD.STORED_COF:
      return 'card';
    case CHECKOUT_PAYMENT_METHOD.NEW_COF:
      return 'add_new_card';
    case CHECKOUT_PAYMENT_METHOD.NONE:
      return null;
    default:
      const _exhaustiveCheck: never = method;
      console.warn('[checkoutFlowTracking] Unknown payment method', method);
      return _exhaustiveCheck;
  }
}

export function mapSelectedPaymentMethod(type: CheckoutPaymentMethod | undefined) {
  switch (type) {
    case CHECKOUT_PAYMENT_METHOD.STORED_COF:
      return 'card';
    case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
      return 'google_pay';
    case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
      return 'apple_pay';
    case CHECKOUT_PAYMENT_METHOD.SWISH:
      return 'swish';
    case CHECKOUT_PAYMENT_METHOD.KLARNA:
      return 'klarna';
    case CHECKOUT_PAYMENT_METHOD.WALLEY:
      return 'walley';
    default:
      return null;
  }
}

export function getSummaryProperties({
  summary,
  selectedPaymentMethod,
  employee,
  isBokadirektPay,
  hasPlatformFee,
}: {
  summary: CheckoutSummary;
  selectedPaymentMethod: SelectedPaymentMethod;
  employee?: CheckoutEmployee;
  isBokadirektPay?: boolean;
  hasPlatformFee?: boolean;
}) {
  const getAppliedGiftcardTypes = (
    giftcards: Giftcard[],
  ): Tracking.BookingFlow['properties']['selected_payment_method'] => {
    const types = [];

    if (giftcards.some((g) => g.code?.startsWith('U-'))) {
      types.push('gift_card');
    }

    if (giftcards.some((g) => g.code?.startsWith('F-'))) {
      types.push('wellness_card');
    }

    if (giftcards.some((g) => g.code?.startsWith('V-'))) {
      types.push('value_card');
    }

    return types;
  };

  const isFree = summary.finalWithoutGiftcards === 0;
  /**
   * shared base properties
   */
  const available_payment_methods: Tracking.BookingFlow['properties']['available_payment_methods'] =
    isFree || !summary.canPayOnline
      ? []
      : summary.availablePaymentMethods.reduce<Tracking.BookingFlow['properties']['available_payment_methods']>(
          (acc, m) => {
            const token = mapPaymentMethod(m.type);
            if (!token) return acc;
            if (token === 'card' && acc.includes('card')) return acc;
            acc.push(token);
            return acc;
          },
          [],
        );

  if (summary.acceptsGiftcard) available_payment_methods.push('gift_card');
  if (summary.acceptsWellnesscard) available_payment_methods.push('wellness_card');
  if (summary.acceptsValuecard) available_payment_methods.push('value_card');

  const preselected_payment_method: Tracking.BookingFlow['properties']['preselected_payment_method'] =
    !isFree && summary.preSelectedPaymentMethod
      ? ([mapPaymentMethod(summary.preSelectedPaymentMethod.type)].filter(
          Boolean,
        ) as Tracking.BookingFlow['properties']['preselected_payment_method'])
      : [];

  const selected_payment_method: Tracking.BookingFlow['properties']['selected_payment_method'] = isFree
    ? ['free']
    : ([mapPaymentMethod(selectedPaymentMethod.type)].filter(
        Boolean,
      ) as Tracking.BookingFlow['properties']['selected_payment_method']);

  /**
   * flow specific properties
   */
  return (
    props:
      | { type: 'booking_flow'; data: BookState & { formFields: FormField[] } }
      | { type: 'buy_bundle_flow'; data: BundleState }
      | { type: 'payment_activation_flow'; data: ConfirmedBooking & { usageReqId: BookState['usageReqId'] } },
  ) => {
    switch (props.type) {
      case 'booking_flow': {
        const booking = props.data;

        const giftcards = Object.values(booking.usageReqId?.giftcards || {});

        if (booking.appliedBundle || booking.hasRemovedAppliedBundle) {
          preselected_payment_method.push('bundle');
        }

        if (booking.appliedBundle) {
          selected_payment_method.push('bundle');
        }

        let days_until_booking: string | undefined = undefined;

        if (booking.time?.timestamp && booking.time?.selected) {
          days_until_booking = getDaysUntilDateTime(new Date(getStartTime(booking.time)));
        }

        let has_campaigns = false;

        if (booking.campaigns.length) {
          const { campaigns, time, place } = booking;
          const { priceListId } = employee?.actual?.about || {};

          const hasServiceCampaigns = booking.services.some((service) => {
            const price = getServicePrice(service, priceListId, place, undefined, time);
            const { campaignService } = getServiceCampaignIfAny(service, campaigns);
            const offerPrice = getServicePrice(service, priceListId, place, campaignService, time);
            return offerPrice !== price;
          });

          const hasProductCampaigns = (campaigns || []).some((campaign) =>
            (campaign?.products || []).some((c) => c.originalPrice !== undefined),
          );

          has_campaigns = hasServiceCampaigns || hasProductCampaigns;
        }

        const result: Tracking.BookingFlow['properties'] = {
          available_payment_methods,
          preselected_payment_method,
          selected_payment_method: selected_payment_method.concat(getAppliedGiftcardTypes(giftcards)),
          has_campaigns,
          days_until_booking,
          number_of_required_fields: booking.formFields.filter((f) => f.required).length,
          is_bokadirekt_pay: isBokadirektPay,
          platform_fee: hasPlatformFee,
        };

        return result;
      }
      case 'payment_activation_flow': {
        const booking = props.data;
        const giftcards = Object.values(booking.usageReqId?.giftcards || {});

        if (booking.appliedBundle) {
          selected_payment_method.push('bundle');
        }

        return {
          available_payment_methods,
          preselected_payment_method,
          selected_payment_method: selected_payment_method.concat(getAppliedGiftcardTypes(giftcards)),
          is_bokadirekt_pay: isBokadirektPay,
          platform_fee: hasPlatformFee,
        };
      }
      case 'buy_bundle_flow': {
        const bundle = props.data;

        const result: Tracking.BuyBundleFlow['properties'] = {
          available_payment_methods,
          preselected_payment_method,
          selected_payment_method,
          has_campaigns: Boolean(bundle.bundle?.finalPrice),
        };

        return result;
      }

      default:
        const _exhaustiveCheck: never = props;
        return _exhaustiveCheck;
    }
  };
}

class BookingCheckoutFlowTrackingSession
  extends CheckoutFlowTrackingSessionBase<'booking_flow'>
  implements BookingFlowTrackingSessionMethods
{
  /** @deprecated Use `track` from `@/helpers/tracking`. */
  searchTime() {
    this.track({ step: 'search_time', step_category: 'selection' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  pickTime() {
    this.track({ step: 'choose_time', step_category: 'selection' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  backToServiceSelection() {
    this.track({ step: 'back_to_service_selection', step_category: 'selection' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  backToEmployeeSelection() {
    this.track({ step: 'back_to_employee_selection', step_category: 'selection' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  backToAddOnSelection() {
    this.track({ step: 'back_to_addon_selection', step_category: 'selection' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  backToTimepicker() {
    this.track({ step: 'back_to_choose_time', step_category: 'summary' });
    this.clearSummaryProperties();
  }

  /**
   * Fires when the checkout summary screen has mounted with its summary
   * properties resolved — `is_bokadirekt_pay` and `platform_fee` included.
   *
   * Without it the earliest event carrying those two is `booking_cta`, a button
   * click, which excludes everyone who saw the fee and left. That is the
   * population a fee-conversion study is about.
   *
   * @deprecated Use `track` from `@/helpers/tracking`.
   */
  summaryViewed() {
    /**
     * Every other event here follows a user action, so `track()` treats a dead
     * session as a bug and reports it to Sentry. This one follows a mount, which
     * a cancelled session outlives — browser-back onto the checkout URL is
     * enough — so it has to bow out quietly instead.
     */
    if (!this.isActive()) return;

    this.track({ step: 'initiated', step_category: 'summary' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  viewServiceDetails(step: 'initiated' | 'cancel', bookable: boolean) {
    this.track({
      step,
      step_category: `view_service_details_${bookable ? 'bookable' : 'unbookable'}`,
    });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  employeeSelected(employee_selection: 'any' | 'specific'): void {
    this.track({ step: 'employee_selected', step_category: 'selection', employee_selection });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  serviceSelected() {
    this.track({ step: 'service_selected', step_category: 'selection' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  addService(step: 'initiated' | 'success' | 'cancel' = 'initiated') {
    this.track({ step, step_category: 'add_service' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  addRecommendedService() {
    this.track({ step: 'add_recommended_service', step_category: 'selection' });
  }

  /** @deprecated Use `track` from `@/helpers/tracking`. */
  removedService() {
    this.track({ step: 'removed_service', step_category: 'selection' });
  }

  /**
   * Sets the timeslots that have been viewed.
   *
   * We need to keep track of this in the session so that
   * we can keep track of the number of unique timeslots viewed
   *
   * @param timeslots - Array of ISO 8601 datetime strings
   *   (e.g. `['2026-01-21T16:30:00+01:00', '2026-01-21T17:00:00+01:00']`)
   */
  appendTimeslotsViewed(timeslots: string[]): void {
    if (!this.state) return;

    const unique = [...new Set([...(this.state.timeslotsViewed || []), ...timeslots])];
    this.state.timeslotsViewed = unique;

    this.setProperties({ timeslots_available: unique?.length ?? 0 });
  }
}
class BuyBundleFlowTrackingSession extends CheckoutFlowTrackingSessionBase<'buy_bundle_flow'> {}
class PaymentActivationFlowTrackingSession extends CheckoutFlowTrackingSessionBase<'payment_activation_flow'> {}

export const bookingFlowTrackingSession = new BookingCheckoutFlowTrackingSession('booking_flow');
export const buyBundleFlowTrackingSession = new BuyBundleFlowTrackingSession('buy_bundle_flow');
export const paymentActivationFlowTrackingSession = new PaymentActivationFlowTrackingSession('payment_activation_flow');
